import { Row, Tab, Tabs, Col, Card, Button } from "react-bootstrap";
import { useState } from "react";
import "./Sales.css";
import { User, Calendar, MapPin } from "lucide-react";
import { useGetCountySalesQuery } from "../../redux/countySlice";
import { format } from "date-fns";
import { useEffect } from "react";
import TaxSaleDetailsModal from "./TaxSaleDetailsModal";
import { getContactInfo } from "../../utilities/contactInfoUtils";
import { SheriffCards } from "./SheriffCards";

function ContactCard({ role, name, phone }) {
  const tel = phone?.replace(/[^\d+]/g, "");
  return (
    <Card className="softCard h-100 w-100">
      <Card.Body className="d-flex gap-3">
        <User size={22} className="orange" />
        <div>
          <div className="text-secondary fs-10">{role}</div>
          <div className="fs-8">{name}</div>
          {phone ? (
            <a href={`tel:${tel}`} className="text-decoration-none orange">
              {phone}
            </a>
          ) : (
            <span>No phone number available</span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// const SheriffCards = ({ name, phone, foreclosureLabel, foreclosurePhone }) => {
//   const sheriffTel = phone?.replace(/[^\d+]/g, "");
//   const foreclosureTel = foreclosurePhone?.replace(/[^\d+]/g, "");

//   return (
//     <>
//       <Col md={6}>
//         <Card className="softCard h-100 w-100">
//           <Card.Body className="d-flex gap-3">
//             <User size={22} className="orange" />
//             <div>
//               <div className="text-secondary fw-medium">Sheriff</div>
//               <div className="fs-8">{name}</div>
//               {foreclosurePhone && phone && (
//                 <a href={`tel:${sheriffTel}`} className="text-decoration-none orange">
//                   {phone}
//                 </a>
//               )}
//             </div>
//           </Card.Body>
//         </Card>
//       </Col>
//       <Col md={6}>
//         <Card className="softCard h-100 w-100">
//           <Card.Body className="d-flex gap-3">
//             <Phone size={22} className="orange" />
//             <div>
//               {foreclosurePhone ? (
//                 <>
//                   <div className="text-secondary fw-medium">{foreclosureLabel}</div>
//                   <a href={`tel:${foreclosureTel}`} className="text-decoration-none orange">
//                     {foreclosurePhone}
//                   </a>
//                 </>
//               ) : (
//                 <>
//                   <div className="text-secondary fw-medium">Sheriff's Office</div>
//                   {phone && (
//                     <a href={`tel:${sheriffTel}`} className="text-decoration-none orange">
//                       {phone}
//                     </a>
//                   )}
//                 </>
//               )}
//             </div>
//           </Card.Body>
//         </Card>
//       </Col>
//     </>
//   );
// };

function SalesTab({ contacts, sales, onViewDetails, emptyState, saleType, sheriffDetails, tsmDetails }) {
  return (
    <div className="taxTab">
      <h5 className="taxSectionTitle mb-3">Contact Information</h5>

      <Row className="g-4 mb-5">
        {saleType === "F" && sheriffDetails?.SheriffName ? (
          <SheriffCards
            name={sheriffDetails?.SheriffName}
            phone={sheriffDetails?.SheriffPhone}
            foreclosureLabel={tsmDetails?.ContactPhone3Label}
            foreclosurePhone={tsmDetails?.ContactPhone3Value}
          />
        ) : contacts.length > 0 ? (
          contacts.map((c) => (
            <Col key={c.role} md={4}>
              <ContactCard role={c.role} name={c.name} phone={c.phone} />
            </Col>
          ))
        ) : (
          <Col md={6}>
            <span>No contact information available</span>
          </Col>
        )}
      </Row>

      <h5 className="taxSectionTitle mb-3">Scheduled Sales</h5>

      {sales.length > 0 ? (
        <div className="d-flex flex-column gap-3">
          {sales.map((s, idx) => (
            <SaleRow key={`${s.date}-${idx}`} date={s.date} mode={s.mode} onView={() => onViewDetails?.(s)} />
          ))}
        </div>
      ) : (
        <Card className="h-100 w-100 p-4 d-flex align-items-center justify-content-center">{emptyState || null}</Card>
      )}
    </div>
  );
}

function SaleRow({ date, mode, onView }) {
  return (
    <Card className="saleRow w-100">
      <Card.Body className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <Calendar size={18} className="orange" />
            <span className="fs-8">{date}</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <MapPin size={18} className="orange" />
            <span className="fs-8">{mode}</span>
          </div>
        </div>
        <Button className="viewDetailsBtn align-self-end align-self-sm-center" onClick={onView}>
          <span className="fs-8">View Details</span>
        </Button>
      </Card.Body>
    </Card>
  );
}

// Keeping static tabInfo outside the component gives it a stable reference, so it doesn't
// need to be listed in useEffect/useMemo dependency arrays.
const tabInfo = [
  { eventKey: "taxSales", title: "Tax Sales", saleType: "A", param: "tax" },
  { eventKey: "foreclosureSales", title: "Foreclosure Sales", saleType: "F", param: "foreclosure" },
  { eventKey: "deedSales", title: "Deed Sales", saleType: "D", param: "deed" },
  { eventKey: "certSales", title: "Certificate Sales", saleType: "C", param: "certificate" },
];

const Sales = ({ stateCode, countyName, sheriffDetails, tsmDetails }) => {
  const [key, setKey] = useState("taxSales");
  const [sales, setSales] = useState([]);
  const [mappedSales, setMappedSales] = useState({});
  const [selectedSale, setSelectedSale] = useState(null);

  const { data, error, isLoading } = useGetCountySalesQuery({ stateCode, countyName });

  const organizeSalesBySaleType = (data) => {
    const allSales = {};
    const foreclosureSales = [];
    const taxSales = [];
    const deedSales = [];
    const certSales = [];
    const other = [];

    data.forEach((sale) => {
      switch (sale.SaleType) {
        case "F":
          foreclosureSales.push(sale);
          break;
        case "A":
        case "O":
          taxSales.push(sale);
          break;
        case "D":
          deedSales.push(sale);
          break;
        case "C":
          certSales.push(sale);
          break;
        default:
          other.push(sale);
          break;
      }
    });

    allSales.foreclosureSales = foreclosureSales;
    allSales.deedSales = deedSales;
    allSales.taxSales = taxSales;
    allSales.certSales = certSales;
    allSales.other = other;

    setSales(allSales);
  };

  useEffect(() => {
    if (data) {
      organizeSalesBySaleType(data);
    }
  }, [data]);

  const contacts = selectedSale ? getContactInfo(selectedSale.SaleType, tsmDetails, stateCode) : [];


  useEffect(() => {
    if (!data || data.length === 0) return;

    const saleTypeToEventKey = tabInfo.reduce((acc, tab) => {
      acc[tab.saleType] = tab.eventKey;
      return acc;
    }, {});

    const grouped = tabInfo.reduce((acc, tab) => {
      acc[tab.eventKey] = [];
      return acc;
    }, {});

    data.forEach((sale) => {
      const saleType = sale.SaleType === "O" ? "A" : sale.SaleType; // Treat "O" (Tax Lien Sales) as "A" (Tax Sales) for grouping
      const eventKey = saleTypeToEventKey[saleType];
      if (eventKey) {
        grouped[eventKey].push(sale);
      }
    });

    setMappedSales(grouped);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModalClose = () => {
    setSelectedSale(null);
  };

  if (isLoading) return <p>Loading...</p>;
  if (error) return <p className="text-danger">Unable to load sales. Please try again later.</p>;

  return (
    <div className="county-sales">
      <Row>
        <Tabs
          id="controlled-tab-example"
          activeKey={key ?? "taxSales"}
          onSelect={(k) => setKey(k ?? "taxSales")}
          className="sales-pill-wrapper salesTabs mb-3"
        >
          {tabInfo.map((tab, index) => {
            return (
              <Tab
                key={tab.eventKey}
                eventKey={tab.eventKey}
                title={`${tab.title} (${mappedSales[tab.eventKey]?.length || 0})`}
              >
                <SalesTab
                  contacts={getContactInfo(tab.saleType, tsmDetails, stateCode)}
                  saleType={tab.saleType}
                  sheriffDetails={sheriffDetails}
                  tsmDetails={tsmDetails}
                  sales={
                    sales[tab.eventKey] && sales[tab.eventKey].length > 0
                      ? sales[tab.eventKey].map((sale) => ({
                          date: format(new Date(sale.Date), "MMMM d, yyyy"),
                          mode: sale.Type,
                          raw: sale,
                        }))
                      : []
                  }
                  onViewDetails={(s) => {
                    setSelectedSale(s.raw);
                  }}
                  emptyState={
                    <p className="text-muted p-0 m-0">{`No ${tab.title.toLocaleLowerCase()} scheduled in the system at this time.`}</p>
                  }
                />
              </Tab>
            );
          })}
        </Tabs>
      </Row>
      <TaxSaleDetailsModal
        sale={selectedSale}
        onHide={handleModalClose}
        contacts={contacts}
        saleTypeParam={tabInfo.find((tab) => tab.saleType === selectedSale?.SaleType)?.param}
        sheriffDetails={sheriffDetails}
        tsmDetails={tsmDetails}
      />
    </div>
  );
};

export default Sales;
